import { Button, Col, Menu, Row, TinyTable } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  LucideBuilding,
  LucideEllipsis,
  LucideHash,
  LucidePlusCircle,
  LucideSquarePen,
  LucideTag,
  LucideTrash,
} from 'lucide-react';

import { GovReportingFacilityRecord } from '../../_graphql/queryGovReportingFacilities';
import * as facilityState from '../../_state/govReportingFacility.state';
import { useTranslations } from '../../i18n/useTranslations';

type FacilityRowMenuProps = { row: GovReportingFacilityRecord };

const FacilityRowMenu = ({ row }: FacilityRowMenuProps) => {
  const t = useTranslations();
  const setEditingId = useSetAtom(facilityState.$editingFacilityAssignment);
  const setDialogOpen = useSetAtom(
    facilityState.$isFacilityAssignmentDialogOpen,
  );
  const deleteFacility = useSetAtom(facilityState.$deleteGovReportingFacility);
  const toast = useToast();

  const handleDelete = async () => {
    try {
      await deleteFacility({ id: row.id });
      toast.success(
        t.complianceView.govReporting.detail.facilityAssignment.dialog.messages
          .deleteSuccess,
      );
    } catch (error) {
      toast.error(
        t.complianceView.govReporting.detail.facilityAssignment.dialog.messages.deleteError(
          error instanceof Error ? error.message : 'Unknown error',
        ),
      );
    }
  };

  return (
    <div className="@FacilityRowMenu contents">
      <Menu
        trigger={
          <Button
            className="px-2"
            size="small"
            color="neutral"
            variant="outline"
            icon={<LucideEllipsis />}
          />
        }
        items={[
          {
            label: t.complianceView.common.actions.edit,
            icon: <LucideSquarePen />,
            onAction: () => {
              setEditingId(row.id);
              setDialogOpen(true);
            },
          },
          {
            label: t.complianceView.common.actions.delete,
            icon: <LucideTrash />,
            className: 'text-failure',
            secondStep: ({ close }) => (
              <Col gap3>
                <div className="text-center text-sm text-neutral/70">
                  {t.complianceView.common.confirmDelete}
                </div>
                <Row gap2>
                  <Button variant="outline" color="neutral" onPress={close}>
                    {t.complianceView.common.actions.cancel}
                  </Button>
                  <Button
                    variant="outline"
                    color="failure"
                    icon={<LucideTrash />}
                    onPress={() => {
                      close();
                      handleDelete();
                    }}
                  >
                    {t.complianceView.common.yesDelete}
                  </Button>
                </Row>
              </Col>
            ),
          },
        ]}
      />
    </div>
  );
};

export const FacilityAssignmentPanel = () => {
  const t = useTranslations();
  const facilities = useAtomValue(facilityState.$govReportingFacilities);
  const setDialogOpen = useSetAtom(
    facilityState.$isFacilityAssignmentDialogOpen,
  );
  const setEditingId = useSetAtom(facilityState.$editingFacilityAssignment);

  return (
    <Col className="@FacilityAssignmentPanel gap-4 rounded-lg border border-neutral/10 p-5">
      <Row className="items-center justify-between">
        <div className="text-sm font-semibold">
          {t.complianceView.govReporting.detail.sections.facilities}
        </div>
        <Button
          size="small"
          icon={<LucidePlusCircle />}
          onPress={() => {
            setEditingId(null);
            setDialogOpen(true);
          }}
        >
          {
            t.complianceView.govReporting.detail.facilityAssignment.actions
              .addFacility
          }
        </Button>
      </Row>
      <TinyTable
        rowKey={(row) => row.id}
        isLoading={facilities.isLoading}
        error={facilities.error}
        columns={[
          {
            key: 'facility',
            label:
              t.complianceView.govReporting.detail.facilityAssignment.columns
                .facility,
            icon: LucideBuilding,
            cell: (row) => (
              <div className="truncate font-medium">
                {row.facilityName || '–'}
              </div>
            ),
          },
          {
            key: 'reportingFacilityId',
            label:
              t.complianceView.govReporting.detail.facilityAssignment.columns
                .reportingFacilityId,
            icon: LucideHash,
            width: 180,
            cell: (row) => (
              <div className="truncate text-neutral/70">
                {row.reportingFacilityId ?? '–'}
              </div>
            ),
          },
          {
            key: 'mlid',
            label:
              t.complianceView.govReporting.detail.facilityAssignment.columns
                .mlid,
            icon: LucideTag,
            width: 120,
            cell: (row) => (
              <div className="truncate text-neutral/70">{row.mlid ?? '–'}</div>
            ),
          },
          {
            key: 'actions',
            label: null,
            preventRowClick: true,
            width: 'max-content',
            cell: (row) => <FacilityRowMenu row={row} />,
          },
        ]}
        data={facilities.data ?? []}
      />
      {!facilities.isLoading &&
        !facilities.error &&
        (facilities.data ?? []).length === 0 && (
          <div className="py-4 text-center text-sm text-neutral/50">
            {
              t.complianceView.govReporting.detail.facilityAssignment.emptyState
                .description
            }
          </div>
        )}
    </Col>
  );
};
